'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { getFeedbackPageLoaded } from '@wix/bi-logger-get-feedback/v2';
import { getFeedbackGenericClick } from '@wix/bi-logger-partners-data/v2';
import webBiLogger from '@wix/web-bi-logger';

import { useCommentsContext } from '@/components/Contexts/CommentsContext';
import { useViewerRole } from '@/components/Contexts/ViewerRoleContext';
import { FEEDBACK_USER_TYPE } from '@/constants/bi';
import { EditorType } from '@/types/feedback';
import { mapEditorVersion } from '@/utils/bi';

type GenericClickParams = Parameters<typeof getFeedbackGenericClick>[0];
type PageLoadedParams = Parameters<typeof getFeedbackPageLoaded>[0];

/** Ambient screen values that change as the user navigates the workbench. */
type ScreenState = {
  breakpoint?: string;
  pageId?: string;
};

/** Optional per-call overrides for the page-loaded event. */
export type ReportPageViewParams = {
  pageId?: string;
  breakpoint?: string;
};

/** CTA-specific fields supplied by each call site. */
export type ReportClickParams = {
  clickType: string;
  text?: string;
  commentId?: string;
  commentHasEmail?: boolean;
  responseId?: string;
};

type BiLoggerContextValue = {
  reportPageView: (override?: ReportPageViewParams) => void;
  reportClick: (params: ReportClickParams) => void;
  setScreen: (screen: ScreenState) => void;
};

const NOOP_VALUE: BiLoggerContextValue = {
  reportPageView: () => {},
  reportClick: () => {},
  setScreen: () => {},
};

const BiLoggerContext = createContext<BiLoggerContextValue | null>(null);

/** Latest visitor id for the BI logger. Not a ref: the id is updated during render so child effects in the same commit see it. */
function createVisitorIdSource(initial: string | null) {
  let current = initial;
  return {
    set(next: string | null) {
      current = next;
    },
    read(): string | undefined {
      return current || undefined;
    },
  };
}

export function BiLoggerContextProvider({
  metaSiteId,
  editorType,
  children,
}: {
  metaSiteId?: string;
  editorType?: EditorType;
  children: ReactNode;
}) {
  const { isPartner } = useViewerRole();
  const {
    state: { visitorId },
  } = useCommentsContext();
  // visitorId arrives after the first render on the client page. The logger
  // reads this source at report time so events still carry the id once the
  // session loads.
  const [visitorIdSource] = useState(() => createVisitorIdSource(visitorId));
  visitorIdSource.set(visitorId);
  const [biLogger] = useState(() =>
    webBiLogger
      .factory()
      .withUoUContext({
        msid: metaSiteId,
        // UoUContext types the getter as () => string; undefined means "not yet known".
        visitorId: visitorIdSource.read as () => string,
      })
      .logger(),
  );
  const biLoggerRef = useRef(biLogger);
  const [screen, setScreenState] = useState<ScreenState>({});

  const enrichment = useMemo(
    () => ({
      feedbackUserType: isPartner
        ? FEEDBACK_USER_TYPE.OWNER
        : FEEDBACK_USER_TYPE.CLIENT,
      editorVersion: mapEditorVersion(editorType),
      msid: metaSiteId,
      breakpoint: screen.breakpoint,
      pageId: screen.pageId,
    }),
    [isPartner, editorType, metaSiteId, screen.breakpoint, screen.pageId],
  );

  const enrichmentRef = useRef(enrichment);
  useEffect(() => {
    enrichmentRef.current = enrichment;
  }, [enrichment]);

  const setScreen = useCallback((next: ScreenState) => {
    setScreenState((prev) =>
      prev.breakpoint === next.breakpoint && prev.pageId === next.pageId
        ? prev
        : next,
    );
  }, []);

  const reportPageView = useCallback((override?: ReportPageViewParams) => {
    const current = enrichmentRef.current;
    const params: PageLoadedParams = {
      feedback_user_type: current.feedbackUserType,
      editor_version: current.editorVersion,
      breakpoint: override?.breakpoint ?? current.breakpoint,
      page_id: override?.pageId ?? current.pageId,
      version: 'v2',
    };
    biLoggerRef.current.report(getFeedbackPageLoaded(params));
  }, []);

  const reportClick = useCallback((params: ReportClickParams) => {
    const current = enrichmentRef.current;
    const payload: GenericClickParams = {
      click_type: params.clickType,
      text: params.text,
      comment_id: params.commentId,
      comment_has_email: params.commentHasEmail,
      response_id: params.responseId,
      feedback_user_type: current.feedbackUserType,
      editor_version: current.editorVersion,
      breakpoint: current.breakpoint,
      page_id: current.pageId,
      msid: current.msid,
      version: 'v2',
    };
    biLoggerRef.current.report(getFeedbackGenericClick(payload));
  }, []);

  const value = useMemo(
    () => ({ reportPageView, reportClick, setScreen }),
    [reportPageView, reportClick, setScreen],
  );

  return <BiLoggerContext value={value}>{children}</BiLoggerContext>;
}

/**
 * Returns the BI reporters. Falls back to no-ops when used outside a
 * BiLoggerContextProvider so analytics never crashes the UI and isolated
 * component tests can render without the full provider tree.
 */
export function useBiLoggerContext(): BiLoggerContextValue {
  return useContext(BiLoggerContext) ?? NOOP_VALUE;
}
